/**
 * Logger Utility
 *
 * Environment-aware logging that:
 * - Development (localhost): Shows all logs
 * - Staging: Shows warnings and errors only
 * - Production: Shows errors only
 *
 * Usage:
 *   import { logger } from '@/utils/logger';
 *   logger.log('Something happened');        // Only in development
 *   logger.warn('Warning message');          // Development + staging
 *   logger.error('Error occurred', error);   // All environments
 *   logger.debug('Debug info');              // Only in development
 *
 * For categorized logging:
 *   logger.auth.log('Auth event');           // [Auth] prefix
 *   logger.store.warn('Store warning');      // [Store] prefix
 */

// Determine environment
const getEnvironment = () => {
  if (typeof window === 'undefined') {
    // SSR/build time - check NODE_ENV
    return process.env.NODE_ENV === 'development' ? 'development' : 'production';
  }

  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }

  if (hostname.includes('staging')) {
    return 'staging';
  }

  // Default to production for any other domain
  return 'production';
};

const ENV = getEnvironment();
const IS_DEV = ENV === 'development';
const IS_STAGING = ENV === 'staging';
const IS_PROD = ENV === 'production';

// No-op function for disabled logging
const noop = () => {};

/**
 * Create a logger with optional prefix
 */
const createLogger = (prefix = '') => {
  const formatPrefix = prefix ? `[${prefix}] ` : '';

  return {
    /**
     * Standard log - development only
     */
    log: IS_DEV
      ? (...args) => console.log(formatPrefix + args[0], ...args.slice(1))
      : noop,

    /**
     * Warning - development and staging
     */
    warn: IS_PROD
      ? noop
      : (...args) => console.warn(formatPrefix + args[0], ...args.slice(1)),

    /**
     * Error - all environments
     */
    error: (...args) => console.error(formatPrefix + args[0], ...args.slice(1)),

    /**
     * Debug - development only (for verbose debugging)
     */
    debug: IS_DEV
      ? (...args) => console.log(`🔍 ${formatPrefix}${args[0]}`, ...args.slice(1))
      : noop,

    /**
     * Info - development only
     */
    info: IS_DEV
      ? (...args) => console.info(formatPrefix + args[0], ...args.slice(1))
      : noop,

    /**
     * Group - development only
     */
    group: IS_DEV
      ? (label) => console.group(formatPrefix + label)
      : noop,

    groupEnd: IS_DEV
      ? () => console.groupEnd()
      : noop,
  };
};

// Main logger (no prefix)
export const logger = {
  ...createLogger(),

  // Environment checks
  isDev: IS_DEV,
  isStaging: IS_STAGING,
  isProd: IS_PROD,
  env: ENV,

  // Categorized loggers with prefixes
  auth: createLogger('Auth'),
  store: createLogger('Store'),
  content: createLogger('Content'),
  websocket: createLogger('WebSocket'),
  api: createLogger('API'),
  cache: createLogger('Cache'),
  token: createLogger('Token'),
  permissions: createLogger('Permissions'),
  intercom: createLogger('Intercom'),
  posthog: createLogger('PostHog'),
  session: createLogger('Session'),
  keycloak: createLogger('Keycloak'),
  router: createLogger('Router'),

  /**
   * Create a custom categorized logger
   * @param {string} category - The category name for the prefix
   */
  create: (category) => createLogger(category),
};

export default logger;
